/**
 * Centre Health → Centre Ops: the Centre Operations report.
 *
 * One table per piece of routine centre work, a row per centre, each drawn for
 * its own question: Maintenance Checklist (which days were missed), Parent
 * Onboarding (which step is behind), Preschool Daily Updates and Newsletter
 * (what was not sent). The Interactions Audit tab shows that page itself,
 * opened on Visit type — it is already the report. The API
 * (oh_operations/centre_ops_report/alfred) narrows the centres to the user's
 * own, so the same page serves HQ and centre teams. Each segment is fetched on
 * its own so a table draws as soon as its numbers arrive — the daily-updates
 * segment is the slow one (tens of seconds on a busy month).
 */
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { fetchCentreOpsReport } from 'api'
import InteractionAuditHqRollup from 'app/pages/InteractionAudit/HqRollup'
import { color } from 'app/theme/tokens'

// What this page fetches. Interactions is not fetched here: that tab shows the
// Interactions Audit page itself, which reads its own numbers.
const SEGMENTS = [
  { id: 'checklist', label: 'Maintenance Checklist' },
  { id: 'parent', label: 'Parent Onboarding' },
  { id: 'updates', label: 'Preschool Daily Updates and Newsletter' }
]
const TABS = [
  { id: 'checklist', label: 'Maintenance Checklist' },
  { id: 'interactions', label: 'Interactions Audit' },
  { id: 'parent', label: 'Parent Work' }
]
const PERIODS = [
  { id: 'week', label: 'This week' },
  { id: 'last', label: 'Last week' },
  { id: 'month', label: 'This month' }
]
const GOOD_FROM = 85
const WARN_FROM = 60
const TRACK = '#EFE6DC'
const LEVEL_COLOR = {
  good: color.positive,
  warn: color.warning,
  bad: color.negative,
  none: color.textFaint
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]
const SLOT_TEXT = {
  done: 'done',
  missed: 'missed',
  not_counted: 'submitted, not counted (photo check)',
  pending: 'still open today'
}

const levelOf = (pct) => {
  if (pct === null || pct === undefined) return 'none'
  if (pct >= GOOD_FROM) return 'good'
  return pct >= WARN_FROM ? 'warn' : 'bad'
}
const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
const fromIso = (value) => {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}
const shortDate = (value) => {
  const d = fromIso(value)
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}
const dayLabel = (value) =>
  `${WEEKDAYS[fromIso(value).getDay()]} ${shortDate(value)}`
const addDays = (d, n) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

const rangeFor = (period) => {
  const today = new Date()
  const monday = addDays(today, -((today.getDay() + 6) % 7))
  if (period === 'last')
    return { from: iso(addDays(monday, -7)), to: iso(addDays(monday, -1)) }
  if (period === 'month')
    return {
      from: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: iso(today)
    }
  return { from: iso(monday), to: iso(today) }
}

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(2, 3, 6),
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2, 2, 6) }
  },
  head: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5)
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  title: { fontSize: 22, fontWeight: 600, color: color.ink },
  periods: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  range: { fontSize: 13, color: color.textMuted },
  tabs: { borderBottom: '1px solid #eee', margin: theme.spacing(1.5, 0) },
  tab: {
    minWidth: 0,
    padding: theme.spacing(1.25, 2),
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: 'none'
  },
  note: {
    fontSize: 13,
    color: color.textMuted,
    marginBottom: theme.spacing(1.5)
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    fontSize: 13,
    color: color.textMuted,
    marginBottom: theme.spacing(1.5),
    alignItems: 'center'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  board: {
    background: color.surface,
    border: `1px solid ${color.border}`,
    borderRadius: 12,
    padding: theme.spacing(0.5, 2, 1),
    overflowX: 'auto'
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    minWidth: 560,
    '& th': {
      fontSize: 12,
      fontWeight: 600,
      color: color.textMuted,
      padding: theme.spacing(1.5, 1),
      textAlign: 'left'
    },
    '& td': {
      padding: theme.spacing(1.25, 1),
      borderTop: `1px solid ${color.border}`,
      verticalAlign: 'middle',
      fontSize: 14
    },
    '& tbody tr': { cursor: 'pointer' },
    '& tbody tr:hover td': { background: color.cream }
  },
  centreCell: { fontWeight: 600, whiteSpace: 'nowrap', color: color.ink },
  num: {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    color: color.textMuted,
    '& b': { color: color.ink }
  },
  mid: { textAlign: 'center !important' },
  sectionHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1, 2),
    margin: theme.spacing(1, 0)
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: color.ink },
  steps: {
    minWidth: 980,
    '& th': { verticalAlign: 'bottom' },
    '& th:nth-child(2), & td:nth-child(2)': {
      borderRight: `2px solid ${color.border}`
    }
  },
  totalRow: {
    cursor: 'default !important',
    '& td': {
      fontWeight: 600,
      borderBottom: `2px solid ${color.border}`,
      background: 'none !important'
    }
  },
  sub: {
    display: 'block',
    fontSize: 12,
    fontWeight: 400,
    color: color.textMuted
  },
  stepBar: {
    height: 10,
    borderRadius: 5,
    background: TRACK,
    overflow: 'hidden',
    minWidth: 56
  },
  stepPct: {
    display: 'block',
    fontSize: 11,
    color: color.textMuted,
    marginTop: 2
  },
  stepNum: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    color: color.ink,
    '& span': { fontWeight: 400, color: color.textMuted }
  },
  strip: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '4px 8px',
    alignItems: 'center',
    fontSize: 11,
    color: color.textMuted,
    '& > span:nth-child(odd)': { whiteSpace: 'nowrap' }
  },
  // Morning / End of day: always ONE line each (Satya, 23 Oct — a month of
  // squares was wrapping to two lines, four rows per centre). The squares
  // share the width and shrink to fit instead of wrapping, so the table also
  // stays inside the window.
  dayLine: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(7px, 16px)',
    gap: 2,
    '& > i': { width: '100%', height: 'auto', aspectRatio: '1 / 1' }
  },
  squares: { display: 'flex', gap: 3, flexWrap: 'wrap' },
  sq: {
    width: 16,
    height: 16,
    borderRadius: 4,
    boxSizing: 'border-box',
    display: 'inline-grid',
    placeItems: 'center',
    fontSize: 10,
    fontWeight: 700,
    flex: 'none',
    lineHeight: 1
  },
  sq_done: { background: color.positive },
  sq_missed: { border: `2px solid ${color.negative}`, color: color.negative },
  sq_not_counted: {
    border: `2px solid ${color.warning}`,
    color: color.warning
  },
  sq_pending: { border: `1.5px dashed ${color.textFaint}` },
  bar: {
    height: 12,
    borderRadius: 6,
    background: TRACK,
    overflow: 'hidden',
    minWidth: 140
  },
  barFill: { height: '100%', borderRadius: 6 },
  muted: { color: color.textMuted, fontSize: 13 },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& h2': { fontSize: 18, fontWeight: 600 }
  },
  block: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${color.border}`,
    '&:last-child': { borderBottom: 'none' }
  },
  blockHead: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    fontSize: 14,
    marginBottom: theme.spacing(0.75),
    '& b': { fontVariantNumeric: 'tabular-nums' }
  },
  todo: {
    listStyle: 'none',
    margin: theme.spacing(1, 0, 0),
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    fontSize: 14
  },
  todoItem: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: theme.spacing(1.25),
    alignItems: 'start'
  },
  count: {
    minWidth: 28,
    textAlign: 'center',
    fontWeight: 700,
    borderRadius: 6,
    padding: '1px 6px',
    fontVariantNumeric: 'tabular-nums'
  },
  countBad: { background: '#FBE3E1', color: color.negative },
  countOk: { background: '#E4F2E8', color: color.positive },
  countWarn: { background: '#FFF1E0', color: color.warning },
  tag: { whiteSpace: 'nowrap', fontSize: 12.5, display: 'inline-block' },
  under: {
    display: 'block',
    fontSize: 12,
    color: color.textMuted,
    marginTop: 2,
    whiteSpace: 'nowrap'
  },
  info: {
    margin: 0,
    paddingLeft: theme.spacing(2.25),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    fontSize: 14
  }
}))

const Bar = ({ pct, classes }) => (
  <div className={classes.bar}>
    <div
      className={classes.barFill}
      style={{ width: `${pct || 0}%`, background: LEVEL_COLOR[levelOf(pct)] }}
    />
  </div>
)

const Square = ({ slot, classes }) => (
  <i
    className={`${classes.sq} ${classes[`sq_${slot.state}`]}`}
    title={`${dayLabel(slot.date)}: ${SLOT_TEXT[slot.state]}`}
  >
    {slot.state === 'missed' ? '✕' : ''}
    {slot.state === 'not_counted' ? '!' : ''}
  </i>
)

const Todo = ({ count, children, classes }) => (
  <li className={classes.todoItem}>
    <span
      className={`${classes.count} ${
        count ? classes.countBad : classes.countOk
      }`}
    >
      {count}
    </span>
    <span>{children}</span>
  </li>
)

const summaryOf = (segment, row) => {
  if (!row) return ''
  if (segment === 'checklist')
    return row.due ? `${row.done} of ${row.due} done` : 'Nothing due yet'
  if (segment === 'parent')
    return row.due
      ? `${row.done} of ${row.due} children fully onboarded`
      : 'No children on the tracker'
  if (!row.items.updates.tracked) return 'Not run at this centre'
  return row.due ? `${row.done} of ${row.due} updates sent` : 'Nothing due yet'
}

const ChecklistPending = ({ row, classes }) => {
  const lines = Object.keys(row.cadences)
    .map((key) => {
      const { label, slots } = row.cadences[key]
      if (!slots.length) return null
      const missed = slots.filter((s) => s.state === 'missed')
      const notCounted = slots.filter((s) => s.state === 'not_counted')
      return (
        <React.Fragment key={key}>
          <Todo count={missed.length} classes={classes}>
            {label} missed
            {missed.length
              ? `: ${missed.map((s) => dayLabel(s.date)).join(', ')}`
              : ''}
          </Todo>
          {notCounted.length > 0 && (
            <Todo count={notCounted.length} classes={classes}>
              {label} submitted but not counted, a photo was a repeat:{' '}
              {notCounted.map((s) => dayLabel(s.date)).join(', ')}
            </Todo>
          )}
        </React.Fragment>
      )
    })
    .filter(Boolean)
  return (
    <ul className={classes.todo}>
      {lines.length ? (
        lines
      ) : (
        <li className={classes.muted}>No checklist was due in this period.</li>
      )}
      <Todo count={row.problems} classes={classes}>
        Problems reported by the centre (checkpoints answered No)
      </Todo>
    </ul>
  )
}

const ParentPending = ({ row, classes }) => {
  const { onboarding } = row.items
  return (
    <ul className={classes.todo}>
      {onboarding.children ? (
        <>
          <Todo
            count={onboarding.children - onboarding.complete}
            classes={classes}
          >
            children still to finish onboarding, out of {onboarding.children} on
            the tracker{onboarding.year ? ` (${onboarding.year})` : ''}
          </Todo>
          {(onboarding.steps || [])
            .filter((step) => step.done < onboarding.children)
            .map((step) => (
              <Todo
                key={step.key}
                count={onboarding.children - step.done}
                classes={classes}
              >
                {STEP_HEADERS[step.key] || step.label} not done yet
              </Todo>
            ))}
        </>
      ) : (
        <li className={classes.muted}>
          No children on this centre&apos;s Parent Onboarding tracker.
        </li>
      )}
    </ul>
  )
}

const UpdatesPending = ({ row, classes }) => {
  const { updates, newsletter } = row.items
  return (
    <ul className={classes.todo}>
      {!updates.tracked && (
        <li className={classes.muted}>
          Preschool Daily Updates: not checked here, this centre does not run
          the full-day preschool programme.
        </li>
      )}
      {updates.tracked && updates.due === 0 && (
        <li className={classes.muted}>
          Preschool Daily Updates: nothing was due
          {updates.from
            ? ', no preschool attendance was marked in this period.'
            : ' yet, today is still open.'}
        </li>
      )}
      {updates.tracked && updates.due > 0 && (
        <Todo count={updates.due - updates.done} classes={classes}>
          Preschool Daily Updates not sent, out of {updates.due} due from{' '}
          {shortDate(updates.from)} to {shortDate(updates.to)}
        </Todo>
      )}
      <Todo count={newsletter.done ? 0 : 1} classes={classes}>
        {newsletter.month} newsletter{' '}
        {newsletter.done
          ? 'is sent (marked final)'
          : `is not sent yet, due by ${shortDate(newsletter.due_by)}`}
      </Todo>
    </ul>
  )
}

// Short column headers, the same wording as the Parent Onboarding page's
// STEP_HEADERS (that file is a whole page, too heavy to import for nine labels).
// A step missing here falls back to the API's full label.
const STEP_HEADERS = {
  orientation: 'Orientation',
  welcome_kit: 'Welcome kit',
  family_1on1: 'Teacher 1:1',
  form_sent: 'Form sent',
  form_submitted: 'Form filled',
  whatsapp_group: 'WA group',
  app_downloaded: 'App downloaded',
  id_card_shared: 'ID card sent',
  director_checkin: 'Director check-in'
}

// One onboarding figure, as a bar or as "done/total" — the reader picks.
const StepCell = ({ done, total, asBars, classes }) => {
  if (!total) return <span className={classes.muted}>-</span>
  if (!asBars)
    return (
      <span className={classes.stepNum}>
        {done}
        <span>/{total}</span>
      </span>
    )
  const pct = Math.round((done / total) * 100)
  return (
    <span title={`${done} of ${total}`}>
      <div className={classes.stepBar}>
        <div
          className={classes.barFill}
          style={{ width: `${pct}%`, background: LEVEL_COLOR[levelOf(pct)] }}
        />
      </div>
      <span className={classes.stepPct}>{pct}%</span>
    </span>
  )
}

// Parent Onboarding, centre by centre: the Parent Onboarding page's own step
// columns, with children done out of children on the tracker. The first column
// is children with EVERY step done — never the step ticks added together.
const OnboardingTable = ({ rows, asBars, onRow, classes }) => {
  const tracked = rows
    .filter((row) => row.items.onboarding.children)
    .sort((a, b) => {
      const share = (r) =>
        r.items.onboarding.complete / r.items.onboarding.children
      return share(a) !== share(b)
        ? share(a) - share(b)
        : a.name.localeCompare(b.name)
    })
  if (!tracked.length)
    return (
      <Typography className={classes.note}>
        No children are on the Parent Onboarding tracker.
      </Typography>
    )
  const steps = tracked[0].items.onboarding.steps || []
  const sum = (pick) =>
    tracked.reduce((n, row) => n + pick(row.items.onboarding), 0)
  const children = sum((o) => o.children)
  const cell = (done, total) => (
    <StepCell done={done} total={total} asBars={asBars} classes={classes} />
  )
  return (
    <div className={classes.board}>
      <table className={`${classes.table} ${classes.steps}`}>
        <thead>
          <tr>
            <th>Centre</th>
            <th className={classes.mid}>Fully onboarded</th>
            {steps.map((step) => (
              <th key={step.key} className={classes.mid} title={step.label}>
                {STEP_HEADERS[step.key] || step.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tracked.length > 1 && (
            <tr className={classes.totalRow}>
              <td className={classes.centreCell}>
                All centres
                <span className={classes.sub}>{children} children</span>
              </td>
              <td className={classes.mid}>
                {cell(
                  sum((o) => o.complete),
                  children
                )}
              </td>
              {steps.map((step, i) => (
                <td key={step.key} className={classes.mid}>
                  {cell(
                    sum((o) => (o.steps || [])[i]?.done || 0),
                    children
                  )}
                </td>
              ))}
            </tr>
          )}
          {tracked.map((row) => {
            const ob = row.items.onboarding
            return (
              <tr key={row.uuid} onClick={onRow(row)}>
                <td className={classes.centreCell}>
                  {row.name}
                  <span className={classes.sub}>
                    {ob.children} children{ob.year ? ` · ${ob.year}` : ''}
                  </span>
                </td>
                <td className={classes.mid}>
                  {cell(ob.complete, ob.children)}
                </td>
                {(ob.steps || []).map((step) => (
                  <td key={step.key} className={classes.mid}>
                    {cell(step.done, ob.children)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// A pill that says sent / not sent yet / not sent, in the colour of the fact.
const Tag = ({ level, children, classes }) => (
  <span
    className={`${classes.count} ${classes.tag} ${
      { ok: classes.countOk, warn: classes.countWarn, bad: classes.countBad }[
        level
      ]
    }`}
  >
    {children}
  </span>
)

const NewsletterTag = ({ newsletter, classes }) => {
  if (newsletter.done)
    return (
      <Tag level="ok" classes={classes}>
        Sent
      </Tag>
    )
  if (newsletter.overdue)
    return (
      <Tag level="bad" classes={classes}>
        Not sent
      </Tag>
    )
  return (
    <Tag level="warn" classes={classes}>
      Not sent yet, due by {shortDate(newsletter.due_by)}
    </Tag>
  )
}

// Preschool Daily Updates and the month's Newsletter, centre by centre: what was
// not sent. Weakest first; centres with nothing due, then centres that do not
// run the programme, at the bottom.
const UpdatesTable = ({ rows, asBars, onRow, classes }) => {
  const order = (r) => {
    if (!r.items.updates.tracked) return 300
    if (!r.due) return 200
    return r.pct
  }
  const sorted = [...rows].sort((a, b) =>
    order(a) !== order(b) ? order(a) - order(b) : a.name.localeCompare(b.name)
  )
  const month = rows.length ? rows[0].items.newsletter.month : 'Newsletter'
  return (
    <div className={classes.board}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Centre</th>
            <th>Preschool Daily Updates sent</th>
            <th>{month} newsletter</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const { updates, newsletter } = row.items
            const missed = updates.due - updates.done
            return (
              <tr key={row.uuid} onClick={onRow(row)}>
                <td className={classes.centreCell}>{row.name}</td>
                <td>
                  {!updates.tracked && (
                    <span className={classes.muted}>
                      Not run at this centre
                    </span>
                  )}
                  {updates.tracked && updates.due === 0 && (
                    <span className={classes.muted}>Nothing due yet</span>
                  )}
                  {updates.tracked && updates.due > 0 && asBars && (
                    <span title={`${updates.done} of ${updates.due} sent`}>
                      <div className={classes.bar} style={{ maxWidth: 220 }}>
                        <div
                          className={classes.barFill}
                          style={{
                            width: `${updates.pct}%`,
                            background: LEVEL_COLOR[levelOf(updates.pct)]
                          }}
                        />
                      </div>
                      <span className={classes.under}>
                        {updates.pct}% ·{' '}
                        {missed ? `${missed} missed` : 'none missed'}
                      </span>
                    </span>
                  )}
                  {updates.tracked && updates.due > 0 && !asBars && (
                    <>
                      <span className={classes.stepNum}>
                        {updates.done}
                        <span>/{updates.due}</span>
                      </span>{' '}
                      <Tag level={missed ? 'bad' : 'ok'} classes={classes}>
                        {missed ? `${missed} missed` : 'none missed'}
                      </Tag>
                    </>
                  )}
                </td>
                <td>
                  <NewsletterTag newsletter={newsletter} classes={classes} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const PENDING = {
  checklist: ChecklistPending,
  parent: ParentPending,
  updates: UpdatesPending
}

// `only="checklist"`: just the Maintenance Checklist table with its period
// chips, no tabs and no other fetches — HQ › Centre Operations › Maintenance
// checklist shows it this way (2026-10-15).
const CentreOpsReport = ({ only, title = 'Centre Operations Report' } = {}) => {
  const segments = only ? SEGMENTS.filter((s) => s.id === only) : SEGMENTS
  const classes = useStyles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const [period, setPeriod] = useState('week')
  const [tab, setTab] = useState(only || 'checklist')
  const [data, setData] = useState({})
  const [open, setOpen] = useState(null) // { uuid, view } | { view: 'info' }
  const [asBars, setAsBars] = useState(true)
  const requestId = useRef(0)
  const range = useMemo(() => rangeFor(period), [period])

  useEffect(() => {
    requestId.current += 1
    const mine = requestId.current
    setData(
      segments.reduce(
        (acc, s) => ({ ...acc, [s.id]: { state: 'loading' } }),
        {}
      )
    )
    segments.forEach(({ id }) => {
      fetchCentreOpsReport({ segment: id, from: range.from, to: range.to })
        .then((res) => {
          if (requestId.current !== mine) return
          const byUuid = {}
          ;(res.centres || []).forEach((row) => {
            byUuid[row.uuid] = row
          })
          setData((prev) => ({
            ...prev,
            [id]: {
              state: 'ok',
              rows: res.centres || [],
              byUuid,
              scope: res.scope
            }
          }))
        })
        .catch(() => {
          if (requestId.current !== mine) return
          setData((prev) => ({
            ...prev,
            [id]: { state: 'error', rows: [], byUuid: {} }
          }))
        })
    })
  }, [range])

  const scope = SEGMENTS.map((s) => data[s.id] && data[s.id].scope).find(
    Boolean
  )
  const rowOf = (segment, uuid) =>
    data[segment] && data[segment].byUuid ? data[segment].byUuid[uuid] : null
  const nameOf = (uuid) => {
    const hit = SEGMENTS.map((s) => rowOf(s.id, uuid)).find(Boolean)
    return hit ? hit.name : ''
  }
  const stateOf = (segment) => (data[segment] ? data[segment].state : 'loading')
  const sortedRows = (segment) =>
    [...((data[segment] && data[segment].rows) || [])].sort((a, b) => {
      const pa = a.pct === null ? 101 : a.pct
      const pb = b.pct === null ? 101 : b.pct
      return pa !== pb ? pa - pb : a.name.localeCompare(b.name)
    })
  const click = (segment) => (row) => () =>
    setOpen({ uuid: row.uuid, view: segment })

  const spinner = (note) => (
    <Box display="flex" alignItems="center" justifyContent="center" py={6}>
      <CircularProgress />
      {note && (
        <Typography className={classes.muted} style={{ marginLeft: 12 }}>
          {note}
        </Typography>
      )}
    </Box>
  )
  const loadFailed = (
    <Typography className={classes.note}>
      Could not load this part of the report. Refresh the page to try again.
    </Typography>
  )

  const barsSwitch = (
    <span>
      {[
        [true, 'Progress bars'],
        [false, 'Numbers']
      ].map(([bars, label]) => (
        <Chip
          key={label}
          label={label}
          clickable
          size="small"
          color={bars === asBars ? 'primary' : 'default'}
          variant={bars === asBars ? 'default' : 'outlined'}
          onClick={() => setAsBars(bars)}
          style={{ marginLeft: 6 }}
        />
      ))}
    </span>
  )

  const checklistBody = () => {
    const state = stateOf('checklist')
    if (state === 'loading') return spinner()
    if (state === 'error') return loadFailed
    const rows = sortedRows('checklist')
    if (!rows.length)
      return (
        <Typography className={classes.note}>
          No centre is mapped to your login.
        </Typography>
      )
    const onRow = click('checklist')
    const hasExtra = (key) => rows.some((row) => row.cadences[key].slots.length)
    return (
      <>
        <div className={classes.legend}>
          <span className={classes.legendItem}>
            <i className={`${classes.sq} ${classes.sq_done}`} /> Done
          </span>
          <span className={classes.legendItem}>
            <i className={`${classes.sq} ${classes.sq_missed}`}>✕</i> Missed
          </span>
          <span className={classes.legendItem}>
            <i className={`${classes.sq} ${classes.sq_not_counted}`}>!</i>{' '}
            Submitted, not counted
          </span>
          <span className={classes.legendItem}>
            <i className={`${classes.sq} ${classes.sq_pending}`} /> Still open
            today
          </span>
        </div>
        <div className={classes.board}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Centre</th>
                <th>
                  Day by day ({shortDate(range.from)} to{' '}
                  {shortDate(
                    data.checklist.rows.length ? range.to : range.from
                  )}
                  )
                </th>
                {hasExtra('weekly') && (
                  <th className={classes.mid}>Weekly deep clean</th>
                )}
                {hasExtra('monthly') && (
                  <th className={classes.mid}>Monthly deep clean</th>
                )}
                <th className={classes.num}>Done</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.uuid} onClick={onRow(row)}>
                  <td className={classes.centreCell}>{row.name}</td>
                  <td>
                    <div className={classes.strip}>
                      <span>Morning</span>
                      <span className={classes.dayLine}>
                        {row.cadences.morning.slots.map((s) => (
                          <Square key={s.date} slot={s} classes={classes} />
                        ))}
                      </span>
                      <span>End of day</span>
                      <span className={classes.dayLine}>
                        {row.cadences.end_of_day.slots.map((s) => (
                          <Square key={s.date} slot={s} classes={classes} />
                        ))}
                      </span>
                    </div>
                  </td>
                  {['weekly', 'monthly'].filter(hasExtra).map((key) => (
                    <td key={key} className={classes.mid}>
                      <span
                        className={classes.squares}
                        style={{ justifyContent: 'center' }}
                      >
                        {row.cadences[key].slots.map((s) => (
                          <Square key={s.date} slot={s} classes={classes} />
                        ))}
                      </span>
                    </td>
                  ))}
                  <td className={classes.num}>
                    {row.due ? (
                      <>
                        <b>{row.done}</b> of {row.due}
                      </>
                    ) : (
                      'Nothing due yet'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  const parentBody = () => {
    const state = stateOf('parent')
    const updatesState = stateOf('updates')
    if (state === 'loading') return spinner()
    if (state === 'error') return loadFailed
    const rows = sortedRows('parent')
    if (!rows.length)
      return (
        <Typography className={classes.note}>
          No centre is mapped to your login.
        </Typography>
      )
    return (
      <>
        <div className={classes.sectionHead}>
          <span className={classes.sectionTitle}>Parent Onboarding</span>
          {barsSwitch}
        </div>
        <div className={classes.legend}>
          Children done out of the children on each centre&apos;s tracker. Fully
          onboarded means every step is done. A current picture, not a period.
        </div>
        <OnboardingTable
          rows={rows}
          asBars={asBars}
          onRow={click('parent')}
          classes={classes}
        />
        <div className={classes.sectionHead}>
          <span className={classes.sectionTitle}>
            Preschool Daily Updates and Newsletter
          </span>
        </div>
        <div className={classes.legend}>
          Daily updates sent out of those due from {shortDate(range.from)} to{' '}
          {shortDate(range.to)}, counted up to yesterday. The newsletter is due
          by the last day of the month.
        </div>
        {updatesState === 'loading' &&
          spinner('Counting daily updates, this takes a few seconds.')}
        {updatesState === 'error' && loadFailed}
        {updatesState === 'ok' && (
          <UpdatesTable
            rows={sortedRows('updates')}
            asBars={asBars}
            onRow={click('updates')}
            classes={classes}
          />
        )}
      </>
    )
  }

  const dialog = () => {
    if (!open) return null
    const close = () => setOpen(null)
    let title = 'What this page shows'
    let body = (
      <ul className={classes.info}>
        <li>
          <b>Maintenance Checklist</b>: checklists submitted out of those that
          fell due, day by day. Morning and end of day are due every day, the
          weekly deep clean on Tuesday, the monthly one on the 5th. Today&apos;s
          checklists count only once they are submitted.
        </li>
        <li>
          <b>Interactions Audit</b>: the Interactions Audit page itself, opened
          on Visit type. It has its own visit date, the period chips above do
          not apply to it.
        </li>
        <li>
          <b>Parent Work</b>: Parent Onboarding, children done per step out of
          the children on each centre&apos;s tracker (its newest academic year),
          with fully onboarded meaning every step is done. Below it, Preschool
          Daily Updates sent out of those due in the period, up to yesterday,
          and whether the month&apos;s newsletter is marked final, due by the
          last day of the month.
        </li>
        <li>
          Bars: green is {GOOD_FROM}% or more done, amber is {WARN_FROM} to{' '}
          {GOOD_FROM - 1}%, red is below {WARN_FROM}%.
        </li>
        <li>HQ sees every centre. A centre team sees only its own centres.</li>
        <li>Click a row to see what is pending at that centre.</li>
      </ul>
    )
    if (open.view !== 'info') {
      const name = nameOf(open.uuid)
      const periodLabel = PERIODS.find((p) => p.id === period).label
      const segment = SEGMENTS.find((s) => s.id === open.view)
      const row = rowOf(open.view, open.uuid)
      const Pending = PENDING[open.view]
      title = `${name} · ${segment.label}`
      body = row ? (
        <>
          <div className={classes.blockHead}>
            <span>{open.view === 'parent' ? 'Today' : periodLabel}</span>
            <b>{summaryOf(open.view, row)}</b>
          </div>
          <Bar pct={row.pct} classes={classes} />
          <Box mt={2} className={classes.muted}>
            What is pending
          </Box>
          <Pending row={row} classes={classes} />
        </>
      ) : (
        <Typography className={classes.note}>
          No numbers for this centre.
        </Typography>
      )
    }
    return (
      <Dialog
        open
        onClose={close}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h2">{title}</Typography>
          <IconButton size="small" onClick={close} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{body}</DialogContent>
      </Dialog>
    )
  }

  return (
    <div className={classes.page}>
      <div className={classes.head}>
        <div className={classes.titleRow}>
          <Typography className={classes.title}>{title}</Typography>
          <IconButton
            size="small"
            onClick={() => setOpen({ view: 'info' })}
            aria-label="What this page shows"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </div>
        {tab !== 'interactions' && (
          <div className={classes.periods}>
            {PERIODS.map((p) => (
              <Chip
                key={p.id}
                label={p.label}
                clickable
                color={p.id === period ? 'primary' : 'default'}
                variant={p.id === period ? 'default' : 'outlined'}
                onClick={() => setPeriod(p.id)}
              />
            ))}
            <span className={classes.range}>
              {shortDate(range.from)} to {shortDate(range.to)}
            </span>
          </div>
        )}
      </div>

      {!only && (
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(e, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              label={t.label}
              classes={{ root: classes.tab }}
            />
          ))}
        </Tabs>
      )}

      {tab !== 'interactions' && scope && !scope.all && (
        <Typography className={classes.note}>
          Showing only: {scope.centres.join(', ')}
        </Typography>
      )}

      {tab === 'checklist' && checklistBody()}
      {tab === 'interactions' && (
        <InteractionAuditHqRollup initialTab="segment" embedded />
      )}
      {tab === 'parent' && parentBody()}
      {dialog()}
    </div>
  )
}

export default CentreOpsReport
